export interface NumberValidationOptions {
  min?: number;
  max?: number;
  integer?: boolean;
}

const MAX_LOGO_BYTES = 25 * 1024 * 1024;
const PNG_SIGNATURE = [137, 80, 78, 71, 13, 10, 26, 10] as const;

export function validateNumberDraft(
  rawValue: string,
  { min, max, integer = false }: NumberValidationOptions = {},
): string | null {
  if (rawValue.trim() === '') return 'Enter a value.';
  const value = Number(rawValue);
  if (!Number.isFinite(value)) return 'Enter a valid number.';
  if (integer && !Number.isSafeInteger(value)) return 'Enter a whole number.';
  if (min !== undefined && value < min) return `Minimum value: ${min}.`;
  if (max !== undefined && value > max) return `Maximum value: ${max}.`;
  return null;
}

export function parseNumberDraft(
  rawValue: string,
  options: NumberValidationOptions = {},
): number | null {
  return validateNumberDraft(rawValue, options) ? null : Number(rawValue);
}

export function validateHexColor(value: string): string | null {
  return /^#[0-9a-fA-F]{6}$/.test(value) ? null : 'Use the format #RRGGBB.';
}

export function validateExportFileName(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return 'Enter a file name.';
  if (trimmed.length > 180) return 'Use no more than 180 characters.';
  const hasControlCharacter = [...trimmed].some((character) => character.charCodeAt(0) < 32);
  if (/[<>:"/\\|?*]/.test(trimmed) || hasControlCharacter) {
    return 'Remove reserved filename characters.';
  }
  if (/[. ]$/.test(trimmed)) return 'The name cannot end with a dot or space.';
  const firstSegment = trimmed.split('.')[0]?.toUpperCase();
  if (firstSegment && /^(CON|PRN|AUX|NUL|COM[1-9]|LPT[1-9])$/.test(firstSegment)) {
    return 'Use a different file name.';
  }
  return null;
}

export function validatePngHeader(bytes: Uint8Array): string | null {
  if (bytes.length < 24 || PNG_SIGNATURE.some((byte, index) => bytes[index] !== byte)) {
    return 'The selected file is not a valid PNG.';
  }
  const isHeader = bytes[12] === 73 && bytes[13] === 72 && bytes[14] === 68 && bytes[15] === 82;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (
    !isHeader ||
    view.getUint32(8) !== 13 ||
    view.getUint32(16) === 0 ||
    view.getUint32(20) === 0
  ) {
    return 'The selected PNG has an invalid header.';
  }
  return null;
}

export async function validatePngFile(file: File): Promise<string | null> {
  if (file.size > MAX_LOGO_BYTES) return 'The PNG logo must be 25 MB or smaller.';
  try {
    return validatePngHeader(new Uint8Array(await file.slice(0, 24).arrayBuffer()));
  } catch {
    return 'Could not read the PNG file.';
  }
}
